import React, { useEffect, useState } from 'react';
import {
  Box, Stack, Typography, TextField, IconButton, Button, Alert,
  FormControlLabel, Switch, Tooltip, Paper, useTheme,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';

import SectionCard from './SectionCard';
import { TableCard, GroupLabel } from '../tabLayout';
import { ToneChip } from '../../common/StatusChip';
import { ListRow, MoveButtons, QuietNote } from './sectionParts';
import { assignKeys } from '../../../utils/fieldKey';
import { AdmissionCycle, OfferTerm } from '../../../types/admissions';

interface Props {
  cycle: AdmissionCycle;
  canEdit: boolean;
  onSave: (patch: Partial<AdmissionCycle>) => Promise<void>;
}

/**
 * Offer terms editor. Each term has a short label (shown as a tick on the
 * offer acceptance dialog), markdown content (rendered on the printed offer
 * letter), and a required flag.
 *
 * Each term's key (what `offer.termsAccepted[].termKey` points at) is
 * assigned automatically on save and never changes afterwards.
 */

const OfferTermsSection: React.FC<Props> = ({ cycle, canEdit, onSave }) => {
  const theme = useTheme();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<OfferTerm[]>([]);

  useEffect(() => {
    if (editing) setDraft(cycle.offerTerms ?? []);
  }, [editing, cycle.offerTerms]);

  const addTerm = () => {
    setDraft([
      ...draft,
      { key: '', label: '', content: '', required: true },
    ]);
  };

  const update = (idx: number, patch: Partial<OfferTerm>) => {
    setDraft(draft.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const remove = (idx: number) => setDraft(draft.filter((_, i) => i !== idx));

  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= draft.length) return;
    const next = [...draft];
    [next[idx], next[target]] = [next[target], next[idx]];
    setDraft(next);
  };

  const hasInvalid = draft.some(t => !t.label.trim() || !t.content.trim());

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ offerTerms: assignKeys(draft, t => t.label, 'term', 40) });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const rows = editing ? draft : (cycle.offerTerms ?? []);

  return (
    <SectionCard
      title="Offer terms"
      description="Parents agree to these when they accept an offer; required ones must be accepted."
      canEdit={canEdit}
      editing={editing}
      onEditToggle={setEditing}
      onSave={save}
      onCancel={() => setDraft(cycle.offerTerms ?? [])}
      saving={saving}
      canSave={!hasInvalid}
      headerActions={
        <Button size="small" startIcon={<Add fontSize="small" />} onClick={addTerm}>
          Add term
        </Button>
      }
    >
      {rows.length === 0 ? (
        <QuietNote>No offer terms — offers go out without asking parents to agree to anything.</QuietNote>
      ) : !editing ? (
        <TableCard>
          {rows.map((term, idx) => (
            <ListRow key={`${term.key}-${idx}`} sx={{ alignItems: 'flex-start', py: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>{term.label}</Typography>
                  <ToneChip label={term.required ? 'Required' : 'Optional'} tone={term.required ? 'highlight' : 'neutral'} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {term.content}
                </Typography>
              </Box>
            </ListRow>
          ))}
        </TableCard>
      ) : (
        <Stack spacing={2}>
          {rows.map((term, idx) => (
            <Paper
              key={`${term.key}-${idx}`}
              variant="outlined"
              sx={{ p: 2, borderRadius: 2, borderColor: theme.palette.divider }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <TextField
                  size="small"
                  value={term.label}
                  onChange={(e) => update(idx, { label: e.target.value })}
                  placeholder="e.g. Fee payment policy"
                  fullWidth
                  InputProps={{ sx: { fontWeight: 600 } }}
                />
                <Stack direction="row">
                  <MoveButtons index={idx} count={rows.length} onMove={(dir) => move(idx, dir)} />
                  <Tooltip title="Remove term">
                    <IconButton size="small" color="error" onClick={() => remove(idx)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 2.5 }} alignItems="center">
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={term.required}
                      onChange={(e) => update(idx, { required: e.target.checked })}
                    />
                  }
                  label={<Typography variant="body2">Required</Typography>}
                />
              </Stack>

              <GroupLabel>Wording</GroupLabel>
              <TextField
                size="small"
                value={term.content}
                onChange={(e) => update(idx, { content: e.target.value })}
                multiline
                minRows={3}
                maxRows={10}
                fullWidth
                placeholder="The parent agrees to pay the admission fee by the deadline…"
                helperText="Simple Markdown formatting is supported on the offer letter."
              />
            </Paper>
          ))}
        </Stack>
      )}
      {editing && hasInvalid && (
        <Alert severity="warning" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
          <Typography variant="body2">
            Each term needs a title and wording.
          </Typography>
        </Alert>
      )}
    </SectionCard>
  );
};

export default OfferTermsSection;
